import { useState, useMemo } from 'react'
import { UTCDate } from '@date-fns/utc'
import { MoreHorizontal, Key, Pencil, ShieldOff, KeyRound, X, Trash2 } from 'lucide-react'
import type { ClientListItem } from '@common/types'
import { deriveClientStatus, type ClientStatus } from '@common/utils/clientStatus'
import { ClientStatusBadge } from '@frontend/components/ui/client-status-badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@frontend/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@frontend/components/ui/dropdown-menu'
import { Button } from '@frontend/components/ui/button'
import { UpdateDetailsModal } from './UpdateDetailsModal'
import { UpdateKeyModal } from './UpdateKeyModal'
import { RevokeKeyModal } from './RevokeKeyModal'
import { DeleteClientModal } from './DeleteClientModal'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { useFilters, useSort, type FilterDefinition, type SortColumnDef } from '@frontend/lib/filters'
import { FilterBar } from '@frontend/components/filters/FilterBar'
import { SearchFilter } from '@frontend/components/filters/SearchFilter'
import { StatusFilter } from '@frontend/components/filters/StatusFilter'

interface ClientsTableProps {
  clients: ClientListItem[]
  isLoading?: boolean
}

// Enriched client type with derived status
interface EnrichedClient extends ClientListItem {
  status: ClientStatus
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—'
  // Parse as UTC to avoid timezone shift
  const date = new UTCDate(dateString)
  // Format in UTC to display the actual stored date
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export function ClientsTable({ clients, isLoading }: ClientsTableProps) {
  // Modal state
  const [selectedClient, setSelectedClient] = useState<ClientListItem | null>(null)
  const [updateDetailsOpen, setUpdateDetailsOpen] = useState(false)
  const [updateKeyOpen, setUpdateKeyOpen] = useState(false)
  const [revokeKeyOpen, setRevokeKeyOpen] = useState(false)
  const [deleteClientOpen, setDeleteClientOpen] = useState(false)
  const { can } = usePermissions()

  // Enrich clients with derived status
  const enrichedClients = useMemo<EnrichedClient[]>(() => {
    return clients.map((client) => ({
      ...client,
      status: deriveClientStatus({
        contractEnd: client.contractEnd,
        keyExpiration: client.keyExpiration,
        keyStatus: client.keyStatus,
        hasKey: client.hasKey,
      }),
    }))
  }, [clients])

  // Define filters with match functions
  const filterDefinitions = useMemo<FilterDefinition<EnrichedClient>[]>(
    () => [
      {
        id: 'search',
        label: 'Search',
        type: 'search',
        match: (client, value) => {
          const search = value as string
          if (!search) return true
          return client.name.toLowerCase().includes(search.toLowerCase())
        },
      },
      {
        id: 'status',
        label: 'Status',
        type: 'multiselect',
        match: (client, value) => {
          const statuses = value as ClientStatus[]
          if (statuses.length === 0) return true
          return statuses.includes(client.status)
        },
      },
    ],
    []
  )

  // Use the filtering hook
  const { filteredData, filterState, setFilter, clearAllFilters, activeFilterCount } = useFilters(
    enrichedClients,
    filterDefinitions
  )

  const sortColumns = useMemo<SortColumnDef<EnrichedClient>[]>(
    () => [
      { id: 'name', accessor: (c) => c.name },
      { id: 'contractStart', accessor: (c) => c.contractStart },
      { id: 'contractEnd', accessor: (c) => c.contractEnd },
    ],
    []
  )

  const { sortedData, toggleSort, isSorted } = useSort(filteredData, sortColumns, {
    field: 'name',
    direction: 'asc',
  })

  // Type-safe accessors for filter values
  const searchValue = (filterState.search as string) || ''
  const statusValue = (filterState.status as ClientStatus[]) || []

  // Action handlers
  const handleUpdateDetails = (client: ClientListItem) => {
    setSelectedClient(client)
    setUpdateDetailsOpen(true)
  }

  const handleUpdateKey = (client: ClientListItem) => {
    setSelectedClient(client)
    setUpdateKeyOpen(true)
  }

  const handleRevokeKey = (client: ClientListItem) => {
    setSelectedClient(client)
    setRevokeKeyOpen(true)
  }

  const handleDeleteClient = (client: ClientListItem) => {
    setSelectedClient(client)
    setDeleteClientOpen(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse text-muted-foreground">Loading clients...</div>
      </div>
    )
  }

  return (
    <>
      {/* Filters */}
      <FilterBar className="mb-4">
        <SearchFilter
          value={searchValue}
          onChange={(value) => setFilter('search', value)}
          placeholder="Search clients..."
        />
        <StatusFilter selected={statusValue} onChange={(value) => setFilter('status', value)} />
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-9 px-2 text-muted-foreground">
            <X className="h-4 w-4 mr-1" />
            Clear filters
          </Button>
        )}
      </FilterBar>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        {sortedData.length} of {clients.length} client{clients.length !== 1 ? 's' : ''}
        {activeFilterCount > 0 && ' (filtered)'}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortableTableHead sorted={isSorted('name')} onSort={() => toggleSort('name')}>
                Client Name
              </SortableTableHead>
              <SortableTableHead sorted={isSorted('contractStart')} onSort={() => toggleSort('contractStart')}>
                Contract Start
              </SortableTableHead>
              <SortableTableHead sorted={isSorted('contractEnd')} onSort={() => toggleSort('contractEnd')}>
                Contract End
              </SortableTableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {activeFilterCount > 0 ? 'No clients match your filters' : 'No clients found'}
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((client) => (
                <TableRow key={client.id}>
                  <TableCell className="font-medium">{client.name}</TableCell>
                  <TableCell>{formatDate(client.contractStart)}</TableCell>
                  <TableCell>{formatDate(client.contractEnd)}</TableCell>
                  <TableCell>
                    <ClientStatusBadge status={client.status} />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleUpdateDetails(client)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Update Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {client.hasKey ? (
                          <>
                            <DropdownMenuItem onClick={() => handleUpdateKey(client)}>
                              <KeyRound className="mr-2 h-4 w-4" />
                              Update Key
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleRevokeKey(client)}
                              className="text-destructive focus:text-destructive"
                            >
                              <ShieldOff className="mr-2 h-4 w-4" />
                              Revoke Key
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <DropdownMenuItem onClick={() => handleUpdateKey(client)}>
                            <Key className="mr-2 h-4 w-4" />
                            Add Key
                          </DropdownMenuItem>
                        )}
                        {can('clients.delete') && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteClient(client)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete Client
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modals */}
      <UpdateDetailsModal client={selectedClient} open={updateDetailsOpen} onOpenChange={setUpdateDetailsOpen} />
      <UpdateKeyModal client={selectedClient} open={updateKeyOpen} onOpenChange={setUpdateKeyOpen} />
      <RevokeKeyModal client={selectedClient} open={revokeKeyOpen} onOpenChange={setRevokeKeyOpen} />
      <DeleteClientModal client={selectedClient} open={deleteClientOpen} onOpenChange={setDeleteClientOpen} />
    </>
  )
}
